import { CircleDashed } from 'lucide-react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'

import { AncillaryDetails } from './AncillaryDetails'
import { SmartBudgetPlan } from './Plan'
import PlanCard from './PlanCard/PlanCard'
import { dentalPlanToPlanData, visionPlanToPlanData } from '../../smartBudgetUtils'
import { type CoverageType, type DentalPlan, type VisionPlan } from '../../types'

interface SmartBudgetAncillaryPlanProps {
  coverageType: 'dental' | 'vision',
  plans: DentalPlan[] | VisionPlan[],
  isLoadingPlans: boolean,
  plansError: GrapeAPIErrorType | null,
  viewingCoverageType: CoverageType | null,
  setViewingCoverageType: (planType: CoverageType | null) => void,
  remainingAllowance: number,
  selectedPlan: DentalPlan | VisionPlan | null,
  onSelectPlan: (plan: DentalPlan | VisionPlan | null) => void,
  planCannotBeChanged: boolean,
}

export function SmartBudgetAncillaryPlan ({
  coverageType,
  plans,
  isLoadingPlans: isLoading,
  plansError: error,
  viewingCoverageType,
  setViewingCoverageType,
  remainingAllowance,
  selectedPlan,
  onSelectPlan,
  planCannotBeChanged,
}: SmartBudgetAncillaryPlanProps) {
  const isVisionPlan = coverageType === 'vision'

  if (isLoading) {
    return (
      <PlanCard.Loading />
    )
  }

  if (error) {
    return (
      <PlanCard.Error coverageType={coverageType} error={error as GrapeAPIErrorType} />
    )
  }

  if (plans.length === 0) {
    return (
      <PlanCard.Empty>
        <div className='flex flex-row items-center justify-center gap-4'>
          <CircleDashed className='w-12 h-12 text-gray-300' />
          <p className='text-gray-500'>Your employer is offering you coverage for {coverageType} plans, but unfortunately no plans are available in you area.</p>
        </div>
      </PlanCard.Empty>
    )
  }

  if (!selectedPlan) {
    return null
  }

  return (
    <SmartBudgetPlan
      allPlans={plans}
      selectedPlan={selectedPlan}
      getPlanKey={(plan) => plan.plan_code}
      planToPlanData={(plan) => isVisionPlan ? visionPlanToPlanData(plan as VisionPlan) : dentalPlanToPlanData(plan as DentalPlan)}
      planToPlanPremium={(plan) => plan.premium}
      remainingAllowance={remainingAllowance}
      coverageType={coverageType}
      viewingCoverageType={viewingCoverageType}
      setViewingCoverageType={setViewingCoverageType}
      onSelectPlan={(plan) => onSelectPlan(plan)}
      renderPlanDetails={(plan) => <AncillaryDetails ancillaryPlan={plan} />}
      popoverTitle={`Other available ${coverageType} plans:`}
      planCannotBeChanged={planCannotBeChanged}
    />
  )
}

SmartBudgetAncillaryPlan.displayName = 'SmartBudget.AncillaryPlan'
